import {
  Box,
  Button,
  Divider,
  IconButton,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Drawer,
  Modal,
  Select,
  Menu,
  MenuItem,
  LinearProgress,
  Skeleton
} from '@mui/material';
import _ from 'lodash';
import { MouseEvent, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { updateSelectedId } from '../../../../redux/slices/selectedGroupSlice';
import { setSelectedValue } from '../../../../redux/slices/selectedValueSlice';
import { fetchMentorTasks } from '../../../../redux/slices/mentorTasksSlice';
import { IRoleObj } from '../../../../redux/slices/groupRolesListSlice';
import { userActions } from '../../../../redux/slices/user-profile/userProfieSlice';
import { taskActions } from '../../../../redux/slices/goals-tasks/getTasksSlice';
import { getUserDetails } from '../../../../utils/orgName';
import { API } from '../../../../api';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import RecruitNewMentor from './RecruitNewMentor';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { getStatusText, getStatusColor, getDisplayName } from '../../components/common';
import MeetingDetails from './MeetingDetails';
import UserProfileDetailsPopup from '../../components/UserProfileDetailsPopup';
import { navigateToChat } from '../NavigateToChat';
import { nanoid } from 'nanoid';
import Leaderboard from './cards/Leaderboard';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import { getAcsUserByCommunicationId } from '../../../../routes/helpers';
import { toast } from 'react-toastify';

type RoleKey = 'mentee' | 'mentor' | 'consultant' | 'unknown';
type ProgramStatusKey = 'not-started' | 'in-progress' | 'completed';

const rolePriority: RoleKey[] = ['mentee', 'mentor', 'consultant', 'unknown'];

const statusRank: Record<ProgramStatusKey, number> = {
  'not-started': 0,
  'in-progress': 1,
  completed: 2
};

interface GroupedProgram {
  id: string;
  name: string;
  roles: RoleKey[];
  active: boolean;
  entries: IRoleObj[];
  status: ProgramStatusKey;
  model?: string;
  details?: any;
  raw?: any;
}

function getItemStatus(item: any): ProgramStatusKey {
  const rawStatus = (item?.status || item?.programStatus || item?.progressStatus || '').toString().toLowerCase();

  if (rawStatus.includes('complete') || rawStatus.includes('done') || rawStatus.includes('finished')) {
    return 'completed';
  }
  if (rawStatus.includes('not')) {
    return 'not-started';
  }

  // fallback para active / completed flags
  if (item?.active === false) return 'not-started';
  if (item?.completed === true) return 'completed';

  // padrão: in-progress
  return 'in-progress';
}

const getObjectiveLookupKeys = (obj: any, idx: number) => {
  const seen = new Set<string>();
  const addKey = (value: any) => {
    if (value === undefined || value === null) return;
    const normalized = String(value).trim();
    if (normalized && !seen.has(normalized)) {
      seen.add(normalized);
    }
  };

  addKey(obj?.objectiveId);
  addKey(obj?.id);
  addKey(obj?._id);
  addKey(obj?.objective);
  addKey(obj?.name);
  addKey(`${obj?.objective || obj?.name || 'objective'}_${idx}`);

  return Array.from(seen);
};

const getObjectiveIdentifier = (obj: any) => {
  if (!obj) return null;
  const key = obj?.objectiveId ?? obj?.id ?? obj?._id ?? obj?.objId ?? obj?.objective ?? obj?.name;
  return key != null ? String(key) : null;
};

const isObjectiveStatusCompleted = (status: string | undefined) => {
  const normalized = (status || '').toString().toLowerCase().trim();
  return normalized === 'completed' || normalized === 'complete' || normalized === 'done' || normalized === 'finished';
};

const toSafeNumber = (value: any): number | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const resolveGoalProgress = (mentee: any) => {
  if (!mentee) return 0;
  return (
    toSafeNumber(mentee?.goalProgress) ??
    toSafeNumber(mentee?.progress?.goalProgress) ??
    toSafeNumber(mentee?.progress?.progress) ??
    toSafeNumber(mentee?.progress) ??
    toSafeNumber(mentee?.progressPercentage) ??
    toSafeNumber(mentee?.progressPercent) ??
    toSafeNumber(mentee?.progressPercentile) ??
    0
  );
};

const getMenteesByObjectiveId = (mentees: any[] | undefined, objectiveId: string) => {
  if (!Array.isArray(mentees) || !objectiveId) return [];
  return mentees.filter((mentee: any) => {
    const matched = Array.isArray(mentee.matchedObjectives) ? mentee.matchedObjectives : [];
    return matched.some((obj: any) => {
      const candidateIds = [obj?.id, obj?.objectiveId, obj?._id, obj?.objective, obj?.name, obj?.objId].filter(Boolean);
      return candidateIds.some((id) => String(id) === String(objectiveId));
    });
  });
};

const DEFAULT_MENTEE_STATS = {
  mentorsEngaged: 0,
  messagesSent: 0,
  meetingsCompleted: 0,
  futureMeetings: 0
};

interface TaskSummary {
  totalLevels: number;
  greenLevels: number;
  timeRemainingMinutes: number;
}

const normalizeDrawerMenteeList = (items: any[] | undefined) => {
  if (!Array.isArray(items)) return [];
  return items.map((item: any) => ({
    ...item,
    id: item?.menteeId || item?.userId || item?.id || nanoid(),
    name: item?.menteeName || item?.displayName || item?.name || '',
    status: item?.status || item?.matchStatus || item?.matchStatusText || 'Ongoing',
    matchedObjectives: Array.isArray(item?.matchedObjectives) ? item.matchedObjectives : [],
    goalProgress: resolveGoalProgress(item)
  }));
};

const buildObjectiveMentorsMap = (entries: any[]) => {
  const map: Record<string, any[]> = {};
  (Array.isArray(entries) ? entries : []).forEach((objective: any, idx: number) => {
    const key =
      getObjectiveIdentifier(objective) ||
      objective?.id ||
      objective?._id ||
      objective?.objectiveId ||
      objective?.objective ||
      objective?.name ||
      `${objective?.objective || objective?.name || 'objective'}_${idx}`;
    if (!key) return;
    const mentors = Array.isArray(objective?.mentors) ? objective.mentors : [];
    map[key] = mentors.map((mentor: any) => ({
      ...mentor,
      matchedObjectives: [
        {
          objective: objective?.objective || objective?.name || '',
          id: key,
          objectiveId: key,
          status: objective?.status || 'Ongoing'
        }
      ],
      objectiveLabel: objective?.objective || objective?.name || '',
      objectiveStatus: objective?.status || 'Ongoing',
      objectiveId: key
    }));
  });
  return map;
};

const getMentorObjectiveTitleFromList = (mentor: any, objectives: any[]) => {
  if (!mentor || !Array.isArray(objectives) || objectives.length === 0) return '';
  const mentorId = mentor?.mid || mentor?.userId || mentor?.id || mentor?.mentorId;
  if (!mentorId) return '';
  const matchedObjective = objectives.find((objective: any) => {
    const mentorsList = Array.isArray(objective?.mentors) ? objective.mentors : [];
    return mentorsList.some((eachMentor: any) => {
      const candidateId = eachMentor?.mid || eachMentor?.userId || eachMentor?.id || eachMentor?.mentorId;
      return candidateId && candidateId === mentorId;
    });
  });
  return matchedObjective?.objective || '';
};

const sanitizeName = (value?: string) => {
  if (!value) return '';
  if (value.includes('@')) {
    return value.split('@')[0];
  }
  return value;
};

const getUserFriendlyName = (user: any) => {
  if (!user) return 'You';
  const fullNameParts = [user?.firstName, user?.lastName].filter(Boolean);
  if (fullNameParts.length) {
    return fullNameParts.join(' ');
  }
  if (user?.displayName) {
    const name = sanitizeName(user.displayName);
    if (name) return name;
  }
  if (user?.name) {
    const name = sanitizeName(user.name);
    if (name) return name;
  }
  if (user?.preferredName) {
    const name = sanitizeName(user.preferredName);
    if (name) return name;
  }
  return 'You';
};

export default function UserProgramsDashboard() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const groupRoleState = useAppSelector((state) => state.groupRoleList);
  const groups = groupRoleState.data || [];

  const userDetails = getUserDetails();
  const { orgId, location } = userDetails;
  const currentUserId = userDetails.userId;
  const isCurrentUserMatch = useCallback(
    (person: any) => {
      if (!person) return false;
      const identifiers = [person?.userId, person?.mid, person?.id, person?.matchId, person?.mentorId, person?.menteeId]
        .filter(Boolean)
        .map((id) => String(id));
      identifiers.push(currentUserId);
      if (!currentUserId) return false;
      return identifiers.includes(String(currentUserId));
    },
    [currentUserId]
  );

  const [roleSelections, setRoleSelections] = useState<Record<string, RoleKey>>({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerProgram, setDrawerProgram] = useState<GroupedProgram | null>(null);
  const [drawerRole, setDrawerRole] = useState<RoleKey>('mentee');
  const communityMembers = useAppSelector((state) => state.acsCommunityUsers.data);
  const personalThreadsArr = useAppSelector((state) => state.acsChannels.personalThreadsArr);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuMentee, setMenuMentee] = useState<any>(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileUser, setProfileUser] = useState<any>(null);
  const [meetingModalOpen, setMeetingModalOpen] = useState(false);
  const [meetingMentee, setMeetingMentee] = useState<any>(null);
  const [objectiveModalOpen, setObjectiveModalOpen] = useState(false);
  const [recruitModalOpen, setRecruitModalOpen] = useState(false);
  const [recruitObjId, setRecruitObjId] = useState('');
  const [recruitObjectiveName, setRecruitObjectiveName] = useState('');
  const [learningGrpId, setLearningGrpId] = useState<string>('');
  const [objectiveMatchesMap, setObjectiveMatchesMap] = useState<Record<string, any[]>>({});
  const [programDetails, setProgramDetails] = useState<any>(null);
  const [savingObjective, setSavingObjective] = useState(false);
  const [objectivesUserCanAdd, setObjectivesUserCanAdd] = useState<any[]>([]);
  const [selectedObjectiveId, setSelectedObjectiveId] = useState('');
  const [menteeList, setMenteeList] = useState<any[]>([]);
  const [drawerObjectiveMentors, setDrawerObjectiveMentors] = useState<Record<string, any[]>>({});
  const [existingKeys, setExistingKeys] = useState<any[]>([]);
  const [expandedObjectives, setExpandedObjectives] = useState<Record<string, boolean>>({});
  const [menteeProgressStats, setMenteeProgressStats] = useState(DEFAULT_MENTEE_STATS);
  const [menteeInsightsStats, setMenteeInsightsStats] = useState<{ xpPoints?: number }>({});
  const [cardTaskSummaries, setCardTaskSummaries] = useState<Record<string, TaskSummary>>({});
  const [cardReady, setCardReady] = useState<Record<string, boolean>>({});
  const [cardMenteeProgress, setCardMenteeProgress] = useState<Record<string, any>>({});
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [leaderboardPeers, setLeaderboardPeers] = useState<any[]>([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  // const [cardReady, setCardReady] = useState<Record<string, boolean>>({});
  const applyDrawerMenteeData = useCallback((list: any[]) => {
    setMenteeList(list);
  }, []);

  const fetchDrawerLearningObjectivesForMentee = useCallback(
    async (grpId: string) => {
      if (!grpId || !orgId) return;
      try {
        const response = await API.getLearningObjectives({ grpId, orgId });
        const payload = response?.data?.data || response?.data || {};
        const entries =
          payload?.learningObjectives?.objectives || payload?.learningObjectives?.data || payload?.objectives || [];
        if (!Array.isArray(entries)) {
          console.warn('Unexpected learning objectives shape', entries);
        }
        const normalizedEntries = Array.isArray(entries) ? entries : [];
        setExistingKeys(normalizedEntries);
        setDrawerObjectiveMentors(buildObjectiveMentorsMap(normalizedEntries));
      } catch (error) {
        console.error('Failed to load learning objectives for mentee drawer', error);
      }
    },
    [orgId]
  );

  const loadLeaderboardPeers = useCallback(
    async (grpId: string) => {
      if (!grpId || !orgId || !location) return;
      setLeaderboardLoading(true);
      try {
        const response = await API.getMenteesPeersHomePage(orgId, location, grpId);
        const payload = response?.data?.data || response?.data || [];
        const entries = Array.isArray(payload) ? payload : [];
        const normalized: any[] = [];
        entries.forEach((item) => {
          if (Array.isArray(item?.menteeDetails)) {
            normalized.push(...item.menteeDetails);
          }
        });
        setLeaderboardPeers(normalized);
      } catch (error) {
        console.error('Failed to load leaderboard peers', error);
      } finally {
        setLeaderboardLoading(false);
      }
    },
    [orgId, location]
  );

  const storedTokens = (() => {
    if (typeof window === 'undefined') return {};
    const tokenString = localStorage.getItem('tokens');
    return tokenString ? JSON.parse(tokenString) : {};
  })();
  const idToken = storedTokens?.id_token;
  const accessToken = storedTokens?.access_token;
  const orgRealId = useAppSelector((state) => state.orgDetails.data?._id);
  const [apiPrograms, setApiPrograms] = useState<any[]>([]);
  const [apiProgramsLoading, setApiProgramsLoading] = useState(false);
  const buildObjectiveMenteesMap = useCallback((raw: any) => {
    const objectives =
      raw?.learningObjectives?.objectives || raw?.learningObjectives?.data || raw?.learningObjectives || [];
    const list = raw?.peers?.menteePeers?.menteeDetails || [];
    const map: Record<string, any[]> = {};
    (Array.isArray(objectives) ? objectives : []).forEach((obj: any, idx: number) => {
      const key =
        getObjectiveIdentifier(obj) ||
        obj?.id ||
        obj?._id ||
        obj?.objective ||
        obj?.name ||
        `${obj?.objective || obj?.name || 'objective'}_${idx}`;
      if (key) {
        map[String(key)] = [...list];
      }
    });
    return map;
  }, []);
  const populateCardCache = useCallback(
    (items: any[]) => {
      items.forEach((item) => {
        const cardId = item.id;
        const detail = item.details;
        cardDataCacheRef.current[cardId] = {
          ...cardDataCacheRef.current[cardId],
          details: detail,
          menteeProgress: item.raw?.progress || item.raw?.goalProgress || null,
          menteeList: item.raw?.peers?.menteePeers?.menteeDetails || []
        };
      });
    },
    [buildObjectiveMenteesMap]
  );

  const flattenProgramPayload = useCallback((payload: any) => {
    const datasets = [
      { key: 'menteeMasterClassDataSet', role: 'mentee' as RoleKey },
      { key: 'menteeMentorshipDataSet', role: 'mentee' as RoleKey },
      { key: 'menteeGuidedDataSet', role: 'mentee' as RoleKey },
      { key: 'mentorMasterClassDataSet', role: 'mentor' as RoleKey },
      { key: 'mentorMentorshipDataSet', role: 'mentor' as RoleKey },
      { key: 'mentorGuidedDataSet', role: 'mentor' as RoleKey }
    ];

    const entries: any[] = [];
    datasets.forEach(({ key, role }) => {
      const list = payload?.[key] ?? [];
      (Array.isArray(list) ? list : []).forEach((item: any) => {
        const detail = (item?.groupDetails && item.groupDetails[0]) || {};
        const id = detail?.grpId || detail?.id || detail?.groupId || item?.grpId || nanoid();
        const goalProgress =
          item?.goalProgress?.goalProgress ?? item?.insights?.goalProgress ?? item?.progress?.goalProgress ?? undefined;

        let status: ProgramStatusKey = 'in-progress';
        if (goalProgress === 100) status = 'completed';
        else if (!goalProgress || goalProgress === 0) status = 'not-started';

        entries.push({
          ...item,
          id,
          name: item?.name || detail?.name || `Program ${id}`,
          role,
          model: item?.model || detail?.model || '',
          status,
          programStatus: status,
          progressStatus: status,
          active: detail?.active ?? true,
          details: detail,
          raw: item
        });
      });
    });
    return entries;
  }, []);

  const getOrganizationsPrograms = useCallback(async () => {
    if (!orgRealId || !idToken || !accessToken) {
      setApiPrograms([]);
      return;
    }
    setApiProgramsLoading(true);
    try {
      const response = await API.getUserProgramList({
        orgId: orgRealId,
        idtoken: idToken,
        authorization: accessToken
      });
      const payload = response?.data?.response?.data?.data || response?.data?.data?.data || {};
      const flattened = flattenProgramPayload(payload);
      setApiPrograms(flattened);
      populateCardCache(flattened);
    } catch (error) {
      console.error('Failed to load user programs', error);
      setApiPrograms([]);
    } finally {
      setApiProgramsLoading(false);
    }
  }, [orgRealId, idToken, accessToken, flattenProgramPayload]);

  useEffect(() => {
    getOrganizationsPrograms();
  }, [getOrganizationsPrograms]);
  const cardDataCacheRef = useRef<
    Record<
      string,
      {
        details?: any;
        menteeList?: any[];
        menteeProgress?: any;
      }
    >
  >({});

  const userLo = useAppSelector((state) => state?.userProfile?.data?.learningObjectives as any);
  const [createdLearningObjectivesList, setCreatedLearningObjectivesList] = useState<any[]>([]);
  const [selectLearningObjectivesList, setSelectLearningObjectivesList] = useState<any[]>([]);
  const userLearningObjectives = useMemo(() => {
    if (!userLo) return [];
    if (Array.isArray(userLo)) return userLo;
    if (Array.isArray(userLo.objectives)) return userLo.objectives;
    return [];
  }, [userLo]);

  const getCardObjectivesInfo = (card: GroupedProgram) => {
    const cardDetails = cardDataCacheRef.current[card.id]?.details;
    const programObjectives = cardDetails?.learningObjectives?.objectives || [];
    const totalLearningObjectives =
      typeof cardDetails?.learningObjectives?.number === 'number'
        ? cardDetails.learningObjectives.number
        : programObjectives.length;

    const objectiveKeySet = new Set<string>();
    programObjectives.forEach((obj: any, idx: number) => {
      const key =
        getObjectiveIdentifier(obj) ||
        obj?.id ||
        obj?._id ||
        obj?.objectiveId ||
        `${obj?.objective || obj?.name || 'objective'}_${idx}`;
      if (key) {
        objectiveKeySet.add(key);
      }
    });

    const completedObjectivesCount =
      userLearningObjectives.reduce((count: number, userObjective: any) => {
        const key =
          getObjectiveIdentifier(userObjective) || userObjective?.id || userObjective?._id || userObjective?.objective;
        if (!key || !objectiveKeySet.has(key)) {
          return count;
        }
        return isObjectiveStatusCompleted(userObjective?.status) ? count + 1 : count;
      }, 0) ?? 0;

    return {
      remainingObjectives: Math.max(totalLearningObjectives - completedObjectivesCount, 0),
      hasObjectiveInfo: typeof cardDetails?.learningObjectives?.number === 'number' || programObjectives.length > 0
    };
  };

  const sourceItems = apiPrograms.length ? apiPrograms : groups;
  const grouped: GroupedProgram[] = useMemo(() => {
    const map = new Map<string, GroupedProgram & { hasNonAdminRole: boolean }>();

    (sourceItems || []).forEach((item: any) => {
      const key = item.id;
      const itemStatus = getItemStatus(item);

      const rawRole: string = (item.role as string) || 'unknown';
      const isAdminRole = rawRole === 'admin';

      let normalizedRole: RoleKey = 'unknown';
      if (rawRole === 'mentee' || rawRole === 'mentor' || rawRole === 'consultant') {
        normalizedRole = rawRole;
      }

      if (!map.has(key)) {
        map.set(key, {
          id: key,
          name: item.name,
          roles: [],
          active: !!item.active,
          entries: [],
          status: itemStatus,
          hasNonAdminRole: !isAdminRole && normalizedRole !== 'unknown',
          model: item.model,
          details: item.details || item.groupDetails?.[0],
          raw: item.raw || item
        });
      }

      const entry = map.get(key)!;
      entry.entries.push(item);
      entry.active = entry.active && !!item.active;

      // status com prioridade (Completed > In Progress > Not Started)
      if (statusRank[itemStatus] > statusRank[entry.status]) {
        entry.status = itemStatus;
      }

      if (!isAdminRole && normalizedRole !== 'unknown' && !entry.roles.includes(normalizedRole)) {
        entry.roles.push(normalizedRole);
      }

      if (!isAdminRole && normalizedRole !== 'unknown') {
        entry.hasNonAdminRole = true;
      }
    });

    // filtra programas que só tenham role admin
    return Array.from(map.values())
      .filter((p) => p.hasNonAdminRole)
      .map(({ hasNonAdminRole, ...rest }) => rest);
  }, [sourceItems]);

  const getEffectiveStatus = (card: GroupedProgram): ProgramStatusKey => {
    const model = (getCardModel(card) || '').toLowerCase();
    const isMasterclassType = model.includes('masterclass') || model.includes('guided');

    if (isMasterclassType) {
      const summary = cardTaskSummaries[card.id];
      const displayPercent =
        summary && summary.totalLevels > 0 ? Math.round((summary.greenLevels / summary.totalLevels) * 100) : undefined;

      if (displayPercent === 0) return 'not-started';
      if (displayPercent === 100) return 'completed';

      return 'in-progress';
    }

    const hasMenteeRole = card.roles.includes('mentee');
    if (!hasMenteeRole) return card.status;

    const progressData = cardMenteeProgress[card.id] ?? cardDataCacheRef.current[card.id]?.menteeProgress ?? null;
    if (!progressData) return card.status;

    const scheduledValue =
      progressData?.meetingsScheduled ??
      progressData?.futureMeetings ??
      progressData?.scheduledMeetings ??
      progressData?.upcomingMeetings ??
      progressData?.meetings ??
      progressData?.meetingsScheduledCount ??
      progressData?.future ??
      progressData?.scheduled ??
      undefined;
    const scheduledMeetings = toSafeNumber(scheduledValue);
    const hasScheduledInfo = scheduledMeetings !== undefined;

    if (hasScheduledInfo && scheduledMeetings === 0) {
      return 'not-started';
    }

    const progressObjectivesValue =
      progressData?.objectivesPending ??
      progressData?.objectivePending ??
      progressData?.objectivesRemaining ??
      progressData?.objectivesYetToBeCompleted ??
      progressData?.remainingObjectives ??
      undefined;
    const normalizedObjectives = toSafeNumber(progressObjectivesValue);
    const hasProgressObjectivesInfo = normalizedObjectives !== undefined;

    const objectivesInfo = getCardObjectivesInfo(card);
    const hasObjectiveInfo = hasProgressObjectivesInfo || objectivesInfo.hasObjectiveInfo;
    const remainingObjectives =
      hasProgressObjectivesInfo && normalizedObjectives !== undefined
        ? normalizedObjectives
        : objectivesInfo.remainingObjectives;

    if (hasObjectiveInfo && remainingObjectives === 0) {
      return 'completed';
    }

    if (hasScheduledInfo || hasObjectiveInfo) {
      return 'in-progress';
    }

    return card.status;
  };

  const computeTaskSummaryFromPayload = (payload: any): TaskSummary | null => {
    const entries = Array.isArray(payload) ? payload : payload ? [payload] : [];
    const levels: any[] = [];
    entries.forEach((entry) => {
      if (Array.isArray(entry?.levels)) {
        levels.push(...entry.levels);
      }
    });
    if (!levels.length) return null;
    const greenLevels = levels.filter((level) => String(level?.levelColor || '').toLowerCase() === 'green').length;
    const grayLevels = levels.length - greenLevels;
    return {
      totalLevels: levels.length,
      greenLevels,
      timeRemainingMinutes: grayLevels * 5
    };
  };

  const shouldFetchTasksForModel = (model?: string) => {
    if (!model) return false;
    const normalized = model.toString().toLowerCase();
    return (
      normalized.includes('masterclass') || normalized.includes('mentorship&masterclass') || normalized === 'mentorship'
    );
  };

  const prefetchCardData = useCallback(
    async (card: GroupedProgram) => {
      const cardId = card.id;
      if (!cardId || !orgId || !location) return;
      setCardReady((prev) => ({ ...prev, [cardId]: false }));

      const cached = cardDataCacheRef.current[cardId] || {};
      const hasMenteeRole = card.roles.includes('mentee');

      let detailPayload = cached.details;
      if (!detailPayload) {
        try {
          const res = await API.getGroupDetails(cardId);
          detailPayload = res?.data?.data || res?.data;
          cardDataCacheRef.current[cardId] = {
            ...cardDataCacheRef.current[cardId],
            details: detailPayload
          };
        } catch {
          /* ignore */
        }
      }

      const fetchPromises: Promise<void>[] = [];

      if (hasMenteeRole && !('menteeProgress' in cached)) {
        fetchPromises.push(
          API.getMenteeProgress({ orgId, grpId: cardId })
            .then((res: any) => {
              const data = res?.data?.data || res?.data;
              setCardMenteeProgress((prev) => ({ ...prev, [cardId]: data }));
              cardDataCacheRef.current[cardId] = {
                ...cardDataCacheRef.current[cardId],
                menteeProgress: data ?? null
              };
            })
            .catch(() => {
              setCardMenteeProgress((prev) => ({ ...prev, [cardId]: null }));
              cardDataCacheRef.current[cardId] = {
                ...cardDataCacheRef.current[cardId],
                menteeProgress: null
              };
            })
        );
      }

      if (!cached.menteeList) {
        fetchPromises.push(
          API.getMenteeListForMentor(orgId, location, cardId)
            .then((res: any) => {
              const data = res?.data?.data || res?.data || [];
              const normalized = normalizeDrawerMenteeList(data);
              cardDataCacheRef.current[cardId] = {
                ...cardDataCacheRef.current[cardId],
                menteeList: normalized
              };
            })
            .catch(() => {
              /* ignore */
            })
        );
      }

      await Promise.all(fetchPromises);

      const cardModel = detailPayload?.model || getCardModel(card);
      if (shouldFetchTasksForModel(cardModel)) {
        try {
          const res = await API.getAllTasks({ grpId: cardId });
          const payload = res?.data?.data || res?.data;
          const summary = computeTaskSummaryFromPayload(payload);
          if (summary) {
            setCardTaskSummaries((prev) => ({ ...prev, [cardId]: summary }));
          }
        } catch {
          /* ignore */
        }
      }

      setCardReady((prev) => ({ ...prev, [cardId]: true }));
    },
    [orgId, location]
  );

  const loadMatchesForRole = useCallback(
    async (grpId: string, model: string, objectives: any[], roleKey: 'mentor' | 'mentee') => {
      if (!grpId || !orgId) {
        setDrawerLoading(false);
        return;
      }
      setDrawerLoading(true);
      const entries = Array.isArray(objectives) ? objectives : [];
      const isGuided = model?.toLowerCase().includes('masterclass&mentorship');

      if (isGuided) {
        try {
          const res = await API.getObjectiveMentors({ orgId, grpId, objId: roleKey });
          const data = res?.data?.data || res?.data || [];
          console.log('[loadMatchesForRole] guided load', {
            group: grpId,
            roleKey,
            count: Array.isArray(data) ? data.length : 0
          });
          const map: Record<string, any[]> = {};
          (Array.isArray(data) ? data : []).forEach((match: any) => {
            const objectivesList =
              match?.extended?.learningObjectives || match?.matchedObjectives || match?.objectives || [];
            (Array.isArray(objectivesList) ? objectivesList : []).forEach((objective: any) => {
              const key = objective?.id || objective?.objectiveId || objective?.objective || objective?.name || roleKey;
              if (!key) return;
              if (!map[String(key)]) map[String(key)] = [];
              map[String(key)].push(match);
            });
          });
          setObjectiveMatchesMap(map);
        } catch (err) {
          console.error('Failed to load guided matches', roleKey, err);
        } finally {
          setDrawerLoading(false);
        }
        return;
      }

      if (roleKey === 'mentor') {
        try {
          const res = await API.getAllObjectiveMentors({ orgId, grpId });
          const data = res?.data?.data || res?.data || [];
          console.log('[loadMatchesForRole] mentor-all load', {
            group: grpId,
            count: Array.isArray(data) ? data.length : 0
          });
          const map: Record<string, any[]> = {};
          (Array.isArray(data) ? data : []).forEach((match: any) => {
            const objectivesList =
              match?.matchedObjectives || match?.extended?.learningObjectives || match?.objectives || [];
            (Array.isArray(objectivesList) ? objectivesList : []).forEach((objective: any) => {
              const objId = objective?.id || objective?.objectiveId || objective?.objective || objective?.name;
              if (!objId) return;
              const key = String(objId);
              if (!map[key]) map[key] = [];
              map[key].push(match);
            });
          });
          setObjectiveMatchesMap(map);
        } catch (err) {
          console.error('Failed to load mentor matches', err);
        } finally {
          setDrawerLoading(false);
        }
        return;
      }

      await Promise.all(
        entries.map(async (obj) => {
          const objId = obj?.id || obj?.objectiveId || obj?.objId || obj?.name;
          if (!objId) return;
          try {
            const res = await API.getObjectiveMentors({ orgId, grpId, objId });
            const data = res?.data?.data || res?.data || [];
            console.log('[loadMatchesForRole] per-objective load', {
              group: grpId,
              objective: objId,
              matches: Array.isArray(data) ? data.length : 0
            });
            const matchesArray = Array.isArray(data) ? data : [];
            console.log('[loadMatchesForRole] per-objective data items', {
              objId,
              matchesArray
            });
            setObjectiveMatchesMap((prev) => {
              const updated = { ...prev };
              const addToMap = (key: string, match: any) => {
                if (!updated[key]) updated[key] = [];
                if (!updated[key].some((existing) => existing?.id === match?.id)) {
                  updated[key].push(match);
                }
              };
              matchesArray.forEach((match) => {
                const objectiveEntries = match?.matchedObjectives || match?.extended?.learningObjectives || [];
                (Array.isArray(objectiveEntries) ? objectiveEntries : []).forEach((objective: any) => {
                  const key = String(
                    objective?.id || objective?.objectiveId || objective?.object || objective?.name || objId
                  );
                  if (key) addToMap(key, match);
                });
                if (!match?.matchedObjectives?.length) {
                  addToMap(String(objId), match);
                }
              });
              console.log('[loadMatchesForRole] updated map keys', {
                objId,
                updatedKeys: Object.keys(updated),
                sample: updated[objId]
              });
              return updated;
            });
          } catch (err) {
            console.error('Failed to load objective matches', objId, err);
          }
        })
      );
      setDrawerLoading(false);
    },
    [orgId]
  );

  useEffect(() => {
    grouped.forEach((card) => {
      prefetchCardData(card);
    });
  }, [grouped, prefetchCardData]);

  const filterNotExistingObjectives = () => {
    const userLoIds = new Set(userLo?.map((lo:any) => lo.id));

    const inUserLo = programDetails.learningObjectives.objectives.filter((lo: any) => userLoIds.has(lo.id));
    const notInUserLo = programDetails.learningObjectives.objectives.filter((lo: any) => !userLoIds.has(lo.id));

    setSelectLearningObjectivesList(notInUserLo);
    setCreatedLearningObjectivesList(inUserLo);
  };

  const getFilteredObjectives = async () => {
    const programObjs = programDetails?.learningObjectives?.objectives || [];

    if (!programObjs.length) {
      setObjectivesUserCanAdd([]);
      setSelectedObjectiveId('');
      return;
    }

    try {
      const userObjectivesRes = await API.UserLearningObjectives({ orgId });
      const fetchedUserObjectives = userObjectivesRes?.data?.data || [];

      const existingKeys = new Set(
        fetchedUserObjectives.map((obj: any) => getObjectiveIdentifier(obj)).filter(Boolean)
      );

      const filtered = programObjs.filter((obj: any) => {
        const key = getObjectiveIdentifier(obj) || obj?.objective || obj?.name;
        return key ? !existingKeys.has(key) : false;
      });

      console.log({ userObjectivesRes });

      setObjectivesUserCanAdd(filtered);
      setSelectedObjectiveId((prev) => {
        if (!prev) return '';
        const isStillAvailable = filtered.some((obj: any) => {
          const key = getObjectiveIdentifier(obj) || obj?.objective || obj?.name;
          return key === prev;
        });
        return isStillAvailable ? prev : '';
      });
    } catch (error) {
      console.error('Failed to fetch user objectives', error);
      setObjectivesUserCanAdd([]);
      setSelectedObjectiveId('');
    }
  };

  const handleAddObjective = async () => {
    if (!selectedObjectiveId) return;
    setSavingObjective(true);
    try {
      const userObjectivesRes = await API.UserLearningObjectives({ orgId });
      const latestUserObjectives = userObjectivesRes?.data?.data || [];

      const existingKeys = new Set(latestUserObjectives.map((obj: any) => getObjectiveIdentifier(obj)).filter(Boolean));

      const selectedOption =
        objectivesUserCanAdd.find(
          (obj: any) => (getObjectiveIdentifier(obj) || obj?.objective || obj?.name) === selectedObjectiveId
        ) || objectivesUserCanAdd.find((obj: any) => (obj?.objective || obj?.name) === selectedObjectiveId);

      const selectedKey = getObjectiveIdentifier(selectedOption) || selectedObjectiveId;

      if (selectedKey && !existingKeys.has(selectedKey)) {
        const newEntry = selectedOption || { objective: selectedObjectiveId };
        const updatedLearningObjectives = [...latestUserObjectives, newEntry];
        await API.saveLearningObjectives({ learningObjectives: updatedLearningObjectives });
        dispatch(userActions.updateObjectives(updatedLearningObjectives));
      }

      await getFilteredObjectives();
      setObjectiveModalOpen(false);
      setSelectedObjectiveId('');
    } catch (err) {
      console.error(err);
    } finally {
      setSavingObjective(false);
    }
  };

  useEffect(() => {
    getFilteredObjectives();
  }, [programDetails]);

  useEffect(() => {
    const data = programDetails?.learningObjectives?.objectives || [];
    if (!data.length) {
      setCreatedLearningObjectivesList([]);
      setExpandedObjectives({});
      return;
    }
    setCreatedLearningObjectivesList(data);
    const firstId = getObjectiveIdentifier(data[0]) || data[0]?.id || data[0]?._id || data[0]?.objective;
    if (!firstId) return;
    setExpandedObjectives((prev) => (prev[firstId] ? prev : { [firstId]: true }));
  }, [programDetails]);

  const drawerObjectiveMenteesMap = useMemo(() => {
    const map: Record<string, any[]> = {};
    (menteeList || []).forEach((mentee) => {
      const matched = mentee?.matchedObjectives || [];
      matched.forEach((obj: any, idx: number) => {
        const key =
          getObjectiveIdentifier(obj) ||
          obj?.objective ||
          obj?.name ||
          obj?.objectiveId ||
          obj?.id ||
          obj?._id ||
          `${obj?.objective || 'objective'}_${idx}`;
        if (!key) return;
        if (!map[key]) map[key] = [];
        map[key].push({
          ...mentee,
          objective: obj?.objective || obj?.name
        });
      });
    });
    return map;
  }, [menteeList]);

  const formatAdviceTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60)
      .toString()
      .padStart(2, '0');
    return `${hours}:${mins} h`;
  };

  const mentorStats = useMemo(() => {
    const menteeIds = new Set(
      (menteeList || []).map((mentee) => mentee?.id || mentee?.menteeId || mentee?.userId).filter(Boolean)
    );

    const messagesSent = (menteeList || []).reduce(
      (sum, mentee) => sum + (mentee?.messagesShared ?? mentee?.messageCount ?? mentee?.messagesCount ?? 0),
      0
    );

    const meetingsCompleted = (menteeList || []).reduce(
      (sum, mentee) => sum + (mentee?.meetingsCompleted ?? mentee?.completedMeetings ?? 0),
      0
    );

    const futureMeetings = (menteeList || []).reduce(
      (sum, mentee) => sum + (mentee?.upcomingMeetings ?? mentee?.futureMeetings ?? 0),
      0
    );

    const adviceMinutes = (menteeList || []).reduce(
      (sum, mentee) => sum + (mentee?.adviceMinutes ?? mentee?.adviceDuration ?? 0),
      0
    );

    const xpPoints = (menteeList || []).reduce(
      (sum, mentee) => sum + (mentee?.xpPoints ?? mentee?.xp ?? mentee?.xpEarned ?? 0),
      0
    );

    return {
      menteesEngaged: menteeIds.size,
      messagesSent,
      meetingsCompleted,
      futureMeetings,
      adviceMinutes,
      xpPoints
    };
  }, [menteeList]);

  const mentorshipStatRows = useMemo(
    () => [
      [
        { label: 'Mentees Engaged', value: mentorStats.menteesEngaged },
        { label: 'Messages Sent', value: mentorStats.messagesSent }
      ],
      [
        { label: 'Meetings Completed', value: mentorStats.meetingsCompleted },
        { label: 'Future Meetings', value: mentorStats.futureMeetings }
      ],
      [
        { label: 'Advice Given', value: formatAdviceTime(mentorStats.adviceMinutes) },
        { label: 'XP Points', value: mentorStats.xpPoints }
      ]
    ],
    [mentorStats]
  );

  const parseNumber = (value: any) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  useEffect(() => {
    if (!drawerOpen) {
      setMenteeProgressStats(DEFAULT_MENTEE_STATS);
      setMenteeInsightsStats({});
    }
  }, [drawerOpen]);

  useEffect(() => {
    if (!drawerProgram || drawerRole !== 'mentee' || !drawerOpen) return undefined;

    let canceled = false;
    const fetchStats = async () => {
      try {
        const response = await API.getMenteeProgress({ orgId, grpId: drawerProgram.id });
        if (!canceled) {
          const data = response?.data?.data || response?.data || {};
          setMenteeProgressStats({
            mentorsEngaged: parseNumber(data?.mentorsEngaged ?? data?.mentorCount ?? data?.mentors ?? 0),
            messagesSent: parseNumber(data?.messagesSent ?? data?.messageCount ?? data?.messages ?? 0),
            meetingsCompleted: parseNumber(
              data?.meetingsCompleted ?? data?.meetingHistory ?? data?.meetingsScheduled ?? 0
            ),
            futureMeetings: parseNumber(data?.futureMeetings ?? data?.upcomingMeetings ?? data?.upcoming ?? 0)
          });
        }
      } catch (error) {
        console.error('Failed to fetch mentee progress stats', error);
      }

      try {
        const insights = await API.getMenteeInsights(orgId, location, drawerProgram.id);
        if (!canceled) {
          const insightData = insights?.data?.data || insights?.data || {};
          setMenteeInsightsStats({ xpPoints: parseNumber(insightData?.xpPoints ?? insightData?.xp ?? 0) });
        }
      } catch (error) {
        console.error('Failed to fetch mentee insights', error);
      }
    };

    fetchStats();

    return () => {
      canceled = true;
    };
  }, [drawerProgram, drawerRole, drawerOpen, orgId, location]);

  const menteeProgramObjectives = programDetails?.learningObjectives?.objectives?.length ?? 0;
  const menteeStatRows = useMemo(
    () => [
      [
        { label: 'Program Objectives', value: menteeProgramObjectives },
        { label: 'Mentors Engaged', value: menteeProgressStats.mentorsEngaged }
      ],
      [
        { label: 'Messages Sent', value: menteeProgressStats.messagesSent },
        { label: 'Meetings Completed', value: menteeProgressStats.meetingsCompleted }
      ],
      [
        { label: 'Future Meetings', value: menteeProgressStats.futureMeetings },
        { label: 'XP Points', value: menteeInsightsStats.xpPoints ?? 0 }
      ]
    ],
    [menteeInsightsStats.xpPoints, menteeProgressStats, menteeProgramObjectives]
  );

  useEffect(() => {
    const objectives = programDetails?.learningObjectives?.objectives || [];
    if (!objectives.length) return;
    const firstId = objectives[0]?.id || objectives[0]?._id || objectives[0]?.objective;
    if (!firstId) return;
    filterNotExistingObjectives();
    setExpandedObjectives((prev) => (prev[firstId] ? prev : { [firstId]: true }));
  }, [programDetails, userLo]);

  useEffect(() => {
    if (!drawerProgram) return;
    const objectiveDefs =
      programDetails?.learningObjectives?.objectives || programDetails?.learningObjectives?.data || [];
    const model =
      programDetails?.model ||
      drawerProgram?.model ||
      drawerProgram?.raw?.model ||
      drawerProgram?.raw?.groupDetails?.[0]?.model ||
      '';
    const viewRole = drawerRole === 'mentor' ? 'mentor' : 'mentee';
    setObjectiveMatchesMap({});
    loadMatchesForRole(drawerProgram.id, model, objectiveDefs, viewRole);
  }, [drawerProgram, drawerRole, programDetails, loadMatchesForRole]);

  const getDefaultRole = (roles: RoleKey[]) => {
    const sorted = [...roles].sort((a, b) => rolePriority.indexOf(a) - rolePriority.indexOf(b));
    return sorted[0] || 'mentee';
  };

  const syncReduxForRole = (card: GroupedProgram, role: RoleKey) => {
    const selectedEntry = card.entries.find((e: any) => e.role === role) || card.entries[0];

    if (!selectedEntry) return;

    dispatch(updateSelectedId(selectedEntry));
    if (selectedEntry.role === 'mentor' || selectedEntry.role === 'consultant') {
      dispatch(fetchMentorTasks({ groupId: selectedEntry.id }));
    }
    dispatch(setSelectedValue(`${selectedEntry.id}_${selectedEntry.role}`));
    localStorage.setItem('lastseen', JSON.stringify(selectedEntry));
  };

  const handleSelectProgram = (cardId: string) => {
    const card = grouped.find((g) => g.id === cardId);
    if (!card) return;

    const selectedRole = roleSelections[card.id] || getDefaultRole(card.roles);
    syncReduxForRole(card, selectedRole);
    navigate(`/app/home/${selectedRole}`);
  };

  const handleManagePortfolio = async (program?: GroupedProgram) => {
    const targetProgram = program || drawerProgram;
    if (!targetProgram) return;
    const roleToUse = program ? roleSelections[targetProgram.id] || getDefaultRole(targetProgram.roles) : drawerRole;
    try {
      await API.updateLastseen({
        id: targetProgram.id,
        role: roleToUse,
        name: targetProgram.name
      });
    } catch (error) {
      console.error('Failed to update last seen', error);
    }
    handleSelectProgram(targetProgram.id);
  };

  const getCompletionPercent = (mentee: any) => {
    const fallbackValues = [
      mentee?.progressPercentage,
      mentee?.progressPercent,
      mentee?.completionPercent,
      mentee?.progress?.percentage,
      mentee?.progress?.completionPercent,
      mentee?.progress?.progressPercentage,
      mentee?.progress?.progress,
      mentee?.percent
    ];
    for (const value of fallbackValues) {
      const normalized = toSafeNumber(value);
      if (normalized !== undefined) {
        return Math.min(100, Math.max(0, normalized));
      }
    }
    return 0;
  };

  const renderDrawerSkeletonLines = (lines = 4) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
      {Array.from({ length: lines }).map((_, idx) => (
        <Skeleton key={`drawer-skel-${idx}`} variant="rectangular" height={20} width="100%" />
      ))}
    </Box>
  );

  const handleStartConversation = (mentee: any) => {
    const communicationId =
      mentee?.communicationId || mentee?.communication_id || mentee?.commId || mentee?.communicationID;
    if (!communicationId) {
      return;
    }
    const listedInCommunity = communityMembers?.find((item) => item?.id?.communicationId === communicationId);
    const isInCommunity = Boolean(listedInCommunity);
    navigateToChat(communicationId, isInCommunity, personalThreadsArr, navigate, dispatch);
  };

  const handleOpenMenu = (event: MouseEvent<HTMLElement>, mentee: any) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuMentee(mentee);
  };
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuMentee(null);
  };

  const handleViewProfile = async (mentee: any) => {
    handleCloseMenu();
    try {
      const userId = mentee?.id || mentee?.menteeId || mentee?.userId;
      if (!userId) {
        setProfileUser(mentee);
        setProfileModalOpen(true);
        return;
      }
      const response = await API.getUserDetailsById(userId);
      setProfileUser(response?.data?.data || mentee);
    } catch (error) {
      console.error('Failed to fetch mentee profile', error);
      setProfileUser(mentee);
    } finally {
      setProfileModalOpen(true);
    }
  };

  const handleMeetingHistory = (mentee: any) => {
    handleCloseMenu();
    setMeetingMentee(mentee);
    setMeetingModalOpen(true);
  };

  const handleCloseMeetingModal = () => {
    setMeetingModalOpen(false);
    setMeetingMentee(null);
  };

  const handleViewMenteeProgress = async () => {
    handleCloseMenu();
    if (isGuidedMasterclassCard && menuMentee && drawerProgram) {
      try {
        const communicationId =
          menuMentee?.communicationId ||
          menuMentee?.communication_id ||
          menuMentee?.commId ||
          menuMentee?.communicationID;
        const acsUser = getAcsUserByCommunicationId(communityMembers, communicationId);
        const menteeUserId = _.get(acsUser, 'id.coId') || menuMentee?.id || menuMentee?.menteeId || menuMentee?.userId;
        if (!menteeUserId) {
          throw new Error('Mentee ID not available');
        }
        const grpId = drawerProgram.id;
        dispatch(taskActions.setUserId({ userId: menteeUserId }));
        dispatch(taskActions.setGroupId({ groupId: grpId }));
        const response = await API.getAllTasks({ grpId, userId: menteeUserId });
        const data = _.get(response, 'data.data', []);
        const levels = _.get(_.head(data), 'levels');
        const levelId = _.get(_.head(levels), '_id');
        const tasks = _.get(_.head(levels), 'tasks');
        const taskId = _.get(_.head(tasks), '_id');
        if (levelId && taskId) {
          const encodedName = encodeURIComponent(getDisplayName(menuMentee) || menuMentee?.displayName || '');
          navigate(`/app/home/mentee/level/${levelId}/task/${taskId}?userName=${encodedName}`);
        }
      } catch (error) {
        toast.error(_.get(error, 'response.data.message', 'Failed to open mentee lessons'));
      }
      return;
    }
    handleManagePortfolio();
  };

  const handleOpenDrawer = (card: GroupedProgram) => {
    setDrawerProgram(card);
    setExpandedObjectives({});
    const defaultRole = roleSelections[card.id] || getDefaultRole(card.roles);
    setDrawerRole(defaultRole);
    setLearningGrpId(card.id);
    setDrawerOpen(true);
    setDrawerLoading(true);
    setObjectiveMatchesMap({});
    setProgramDetails(null);

    syncReduxForRole(card, defaultRole);

    const cached = cardDataCacheRef.current[card.id] || {};
    const detail = cached.details || card.details || card.raw?.groupDetails?.[0] || card.raw?.details || {};
    const progressData = cached.menteeProgress || card.raw?.progress || card.raw?.goalProgress || null;

    setProgramDetails(detail || null);
    if (progressData) {
      setCardMenteeProgress((prev) => ({ ...prev, [card.id]: progressData }));
    }

    const menteeList = cached.menteeList || card.raw?.peers?.menteePeers?.menteeDetails || [];
    const normalizedList = normalizeDrawerMenteeList(menteeList);
    applyDrawerMenteeData(normalizedList);
    const viewRole = defaultRole === 'mentor' ? 'mentor' : 'mentee';
    const objectiveDefs = detail?.learningObjectives?.objectives || detail?.learningObjectives?.data || [];
    loadMatchesForRole(card.id, detail?.model || card.model || '', objectiveDefs, viewRole);
  };

  const fetchDrawerMentorMentees = useCallback(
    async (grpId: string) => {
      if (!grpId || !orgId || !location) return;
      try {
        const response = await API.getMenteeListForMentor(orgId, location, grpId);
        const data = response?.data?.data || response?.data || [];
        applyDrawerMenteeData(normalizeDrawerMenteeList(data));
      } catch (error) {
        console.error('Failed to fetch mentee list for mentor drawer', error);
      }
    },
    [orgId, location, applyDrawerMenteeData]
  );

  const fetchDrawerGuidedMasterclassMentees = useCallback(
    async (grpId: string) => {
      if (!grpId || !orgId) return;
      try {
        const response = await API.getMyMentees({ orgId, groupId: grpId });
        const data = response?.data?.data || response?.data || [];
        applyDrawerMenteeData(normalizeDrawerMenteeList(data));
      } catch (error) {
        console.error('Failed to fetch guided masterclass mentees', error);
      }
    },
    [orgId, applyDrawerMenteeData]
  );

  const drawerModelValue =
    programDetails?.model ||
    drawerProgram?.model ||
    drawerProgram?.raw?.model ||
    drawerProgram?.raw?.groupDetails?.[0]?.model ||
    '';
  const isGuidedMasterclassDrawer = useMemo(() => {
    const normalized = drawerModelValue?.toString().toLowerCase() || '';
    return normalized.includes('guided') || (normalized.includes('masterclass') && normalized.includes('mentorship'));
  }, [drawerModelValue]);

  const fetchDrawerGuidedMasterclassMentors = useCallback(
    async (grpId: string) => {
      if (!grpId || !orgId) return;
      try {
        const response = await API.getMyMentors({ orgId, groupId: grpId });
        const data = response?.data?.data || response?.data || [];
        applyDrawerMenteeData(normalizeDrawerMenteeList(data));
      } catch (error) {
        console.error('Failed to fetch guided masterclass mentors', error);
      }
    },
    [orgId, applyDrawerMenteeData]
  );

  useEffect(() => {
    if (!drawerOpen || !drawerProgram?.id) return;
    if (drawerRole === 'mentor') {
      if (isGuidedMasterclassDrawer) {
        fetchDrawerGuidedMasterclassMentees(drawerProgram.id);
      } else {
        fetchDrawerMentorMentees(drawerProgram.id);
      }
      return;
    }

    if (drawerRole === 'mentee' && isGuidedMasterclassDrawer) {
      fetchDrawerGuidedMasterclassMentors(drawerProgram.id);
    }
  }, [
    drawerOpen,
    drawerRole,
    drawerProgram?.id,
    isGuidedMasterclassDrawer,
    fetchDrawerMentorMentees,
    fetchDrawerGuidedMasterclassMentees,
    fetchDrawerGuidedMasterclassMentors
  ]);

  useEffect(() => {
    if (!drawerOpen || drawerRole !== 'mentee' || !learningGrpId || !drawerProgram) return;
    const drawerModel = getCardModel(drawerProgram) || '';
    const isMentorshipDrawer = drawerModel.toString().toLowerCase().includes('mentorship');
    if (!isMentorshipDrawer) return;

    fetchDrawerLearningObjectivesForMentee(learningGrpId);
  }, [drawerOpen, drawerRole, learningGrpId, drawerProgram, fetchDrawerLearningObjectivesForMentee]);

  useEffect(() => {
    if (!drawerOpen || !learningGrpId || !drawerProgram) return;
    const drawerModel = getCardModel(drawerProgram) || '';
    const isMasterclassDrawer = drawerModel.toString().toLowerCase().includes('masterclass');
    if (!isMasterclassDrawer) return;
    loadLeaderboardPeers(learningGrpId);
  }, [drawerOpen, learningGrpId, drawerProgram, loadLeaderboardPeers]);

  const renderRoleToggle = (card: GroupedProgram) => {
    const roles = card.roles.filter((r) => r !== 'unknown');
    if (!roles.length) return null;

    if (roles.length < 2) {
      return (
        <Box
          sx={{
            px: '8px',
            py: '5px',
            borderRadius: '6px',
            background: '#444444',
            color: '#FDFDFD',
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'capitalize'
          }}
        >
          {roles[0] || 'member'}
        </Box>
      );
    }

    const value = roleSelections[card.id] || getDefaultRole(roles);

    return (
      <ToggleButtonGroup
        size="small"
        value={value}
        exclusive
        onChange={(_, val: RoleKey | null) => {
          if (!val) return;
          setRoleSelections((prev) => ({ ...prev, [card.id]: val }));
          syncReduxForRole(card, val);
        }}
        sx={{
          background: '#2A2A2A',
          borderRadius: '6px',
          overflow: 'hidden',
          '& .MuiToggleButton-root': {
            color: '#B6B6B6',
            fontSize: 10,
            fontWeight: 400,
            textTransform: 'none',
            px: 1,
            '&.Mui-selected': {
              background: '#444444',
              color: '#FDFDFD',
              fontWeight: 700
            }
          }
        }}
      >
        {roles.map((role) => (
          <ToggleButton key={role} value={role}>
            {role.charAt(0).toUpperCase() + role.slice(1)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  };

  const getCardModel = (card: GroupedProgram) => {
    const cached = cardDataCacheRef.current[card.id]?.details;
    return cached?.model || cached?.goals?.model || card.model;
  };

  const getProgramTag = (card: GroupedProgram, overrideModel?: string) => {
    const rawType: string = overrideModel || card.model || 'mentorship';

    const lower = rawType.toString().toLowerCase();

    if (lower.includes('masterclass') && lower.includes('mentorship')) {
      return {
        label: 'Guided Masterclass',
        borderColor: '#F15A26',
        textColor: 'black'
      };
    }
    if (lower.includes('guided')) {
      return {
        label: 'Guided Masterclass',
        borderColor: '#F15A26',
        textColor: 'black'
      };
    }
    if (lower.includes('masterclass')) {
      return {
        label: 'Masterclass Program',
        borderColor: '#F0B80D',
        textColor: 'black'
      };
    }

    // default mentorship
    return {
      label: 'Mentorship Program',
      borderColor: '#8E44FF',
      textColor: 'white'
    };
  };

  const renderCard = (card: GroupedProgram, columnStatus: ProgramStatusKey) => {
    const selectedRole = roleSelections[card.id] || getDefaultRole(card.roles);
    const cardModel = getCardModel(card);
    const { label: tagLabel, borderColor, textColor } = getProgramTag(card, cardModel);
    const isMentorshipProgram = tagLabel === 'Mentorship Program';
    const isGuidedMasterclass = tagLabel === 'Guided Masterclass';
    const isMasterclassProgram = tagLabel === 'Masterclass Program';
    const handleContinueClick = (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (isMentorshipProgram) {
        handleOpenDrawer(card);
        return;
      }
      handleManagePortfolio(card);
    };

    // tenta ler progress se existir
    const anyEntry: any = card.entries[0] || {};
    const rawProgress = anyEntry?.progress ?? anyEntry?.programProgress ?? anyEntry?.completionPercent ?? null;
    const progressValue =
      typeof rawProgress === 'number'
        ? Math.max(0, Math.min(100, rawProgress))
        : columnStatus === 'completed'
        ? 100
        : undefined;

    const isCompleted = columnStatus === 'completed';
    const taskSummary = cardTaskSummaries[card.id];
    const summaryPercent =
      taskSummary && taskSummary.totalLevels > 0
        ? Math.round((taskSummary.greenLevels / taskSummary.totalLevels) * 100)
        : undefined;
    const displayPercent = summaryPercent ?? progressValue;
    const timeRemainingText = taskSummary ? `~ ${taskSummary.timeRemainingMinutes} minutes left to complete` : '';
    const { remainingObjectives, hasObjectiveInfo } = getCardObjectivesInfo(card);
    const progressPercent = Math.max(0, Math.min(100, typeof displayPercent === 'number' ? displayPercent : 0));
    const statusLabel = card.status || (isCompleted ? 'Completed' : 'In Progress');
    const showMentorshipMenteeObjectives = isMentorshipProgram && selectedRole === 'mentee' && hasObjectiveInfo;
    const progressBar = (
      <Box
        sx={{
          alignSelf: 'stretch',
          height: 6,
          background: '#2A2A2A',
          borderRadius: 3,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            width: `${progressPercent}%`,
            height: '100%',
            background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)'
          }}
        />
      </Box>
    );
    const renderMentorStatsFooter = () => {
      const showContinueButton = !(isGuidedMasterclass && selectedRole === 'mentor');
      return (
        <Box
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: '10px'
          }}
        >
          <Box
            sx={{
              flex: '1 1 0',
              display: 'flex',
              flexDirection: 'column',
              gap: '5px'
            }}
          >
            <Box sx={{ color: '#868686', fontSize: 14, fontWeight: 400 }}>{mentorStats.messagesSent} Messages Sent</Box>
            <Box sx={{ color: '#868686', fontSize: 14, fontWeight: 400 }}>
              {mentorStats.menteesEngaged} Mentees Engaged
            </Box>
            <Box sx={{ color: '#868686', fontSize: 14, fontWeight: 400 }}>
              {mentorStats.futureMeetings} Meetings Scheduled
            </Box>
          </Box>
          {showContinueButton && (
            <Button
              onClick={handleContinueClick}
              sx={{
                px: '10px',
                py: '4px',
                background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
                borderRadius: '4px',
                color: '#FDFDFD',
                fontSize: 12,
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              Continue Program
            </Button>
          )}
        </Box>
      );
    };
    const renderMentorshipMenteeFooter = () => (
      <>
        <Box
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            gap: '7px'
          }}
        >
          <Box sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 400 }}>{statusLabel}</Box>
          {progressBar}
        </Box>
        <Box
          sx={{
            alignSelf: 'stretch',
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px'
          }}
        >
          <Box sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 600 }}>
            {remainingObjectives} Objectives yet to be completed
          </Box>
          <Button
            onClick={handleContinueClick}
            sx={{
              px: '10px',
              py: '4px',
              background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
              borderRadius: '4px',
              color: '#FDFDFD',
              fontSize: 12,
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Continue Program
          </Button>
        </Box>
      </>
    );
    const renderGuidedMenteeFooter = () => (
      <>
        <Box
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            gap: '7px'
          }}
        >
          <Box sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 400 }}>{`Program Progress: ${progressPercent}%`}</Box>
          {progressBar}
        </Box>
        <Box
          sx={{
            alignSelf: 'stretch',
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px'
          }}
        >
          <Box sx={{ color: '#868686', fontSize: 12, fontWeight: 600 }}>{timeRemainingText}</Box>
          <Button
            onClick={handleContinueClick}
            sx={{
              px: '10px',
              py: '4px',
              background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
              borderRadius: '4px',
              color: '#FDFDFD',
              fontSize: 12,
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Continue Program
          </Button>
        </Box>
      </>
    );
    const renderMasterclassMenteeFooter = () => (
      <>
        <Box
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            gap: '7px'
          }}
        >
          <Box sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 400 }}>{timeRemainingText}</Box>
          {progressBar}
        </Box>
        <Box
          sx={{
            alignSelf: 'stretch',
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px'
          }}
        >
          <Box sx={{ color: '#868686', fontSize: 12, fontWeight: 600 }}>{' '}</Box>
          <Button
            onClick={handleContinueClick}
            sx={{
              px: '10px',
              py: '4px',
              background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
              borderRadius: '4px',
              color: '#FDFDFD',
              fontSize: 12,
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Continue Program
          </Button>
        </Box>
      </>
    );
    const renderDefaultFooter = () => (
      <>
        <Box
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            gap: '7px'
          }}
        >
          <Typography
            sx={{
              flex: '1 1 0',
              color: '#B6B6B6',
              fontSize: 14,
              fontWeight: 400
            }}
          >
            {statusLabel}
          </Typography>
          {progressBar}
        </Box>
        <Box
          sx={{
            alignSelf: 'stretch',
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '10px'
          }}
        >
          <Button
            onClick={handleContinueClick}
            sx={{
              px: '10px',
              py: '4px',
              background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
              borderRadius: '4px',
              color: '#FDFDFD',
              fontSize: 12,
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Continue Program
          </Button>
        </Box>
      </>
    );

    return (
      <Box
        key={`${card.id}-${columnStatus}`}
        sx={{
          width: '100%',
          // maxWidth: 360, // 👈 mantém alinhamento central
          alignSelf: 'center',
          height: 208,
          p: '20px',
          background: '#151515',
          borderRadius: '5px',
          outline: '2px solid #2A2A2A',
          outlineOffset: '-2px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          justifyContent:
            columnStatus === 'in-progress' || columnStatus === 'not-started' ? 'flex-start' : 'space-between',
          opacity: isCompleted ? 0.4 : 1,
          cursor: 'pointer'
        }}
        onClick={() => handleOpenDrawer(card)}
      >
        {/* HEADER + TITLE */}
        <Box
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}
        >
          <Box
            sx={{
              alignSelf: 'stretch',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            {/* TAG */}
            <Box
              sx={{
                px: '8px',
                py: '3px',
                borderRadius: '20px',
                outline: `1px solid ${borderColor}`,
                outlineOffset: '-1px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: borderColor
              }}
            >
              <Typography
                sx={{
                  color: textColor,
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: '14px'
                }}
              >
                {tagLabel}
              </Typography>
            </Box>

            {/* ROLE */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Typography
                sx={{
                  color: '#868686',
                  fontSize: 12,
                  fontWeight: 400
                }}
              >
                Role:
              </Typography>
              {renderRoleToggle(card)}
            </Box>
          </Box>

          {/* TITLE */}
          <Typography
            sx={{
              alignSelf: 'stretch',
              color: '#FDFDFD',
              fontSize: 22,
              fontWeight: 600,
              textTransform: 'capitalize',
              lineHeight: '26.4px'
            }}
          >
            {card.name}
          </Typography>
        </Box>

        {isMentorshipProgram && selectedRole === 'mentor'
          ? renderMentorStatsFooter()
          : isMentorshipProgram && selectedRole === 'mentee'
          ? renderMentorshipMenteeFooter()
          : isGuidedMasterclass && selectedRole === 'mentee'
          ? renderGuidedMenteeFooter()
          : isMasterclassProgram && selectedRole === 'mentee'
          ? renderMasterclassMenteeFooter()
          : isGuidedMasterclass && selectedRole === 'mentor'
          ? renderMentorStatsFooter()
          : renderDefaultFooter()}
      </Box>
    );
  };

  const renderPlaceholderCard = (key: string) => (
    <Skeleton key={`placeholder-${key}`} variant="rounded" sx={{ width: '100%', height: 208, borderRadius: '5px' }} />
  );

  if (groupRoleState.loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  const columns: { key: ProgramStatusKey; title: string }[] = [
    { key: 'not-started', title: 'Not Started' },
    { key: 'in-progress', title: 'In Progress' },
    { key: 'completed', title: 'Completed' }
  ];

  const drawerCardModel = drawerProgram ? getCardModel(drawerProgram) || '' : '';
  const drawerProgramTag = drawerProgram
    ? getProgramTag(drawerProgram, drawerCardModel)
    : { label: 'Mentorship Program', borderColor: '#8E44FF' };
  const isMentorDrawer = drawerRole === 'mentor';
  const isMenteeDrawer = drawerRole === 'mentee';

  const drawerTaskSummary = drawerProgram ? cardTaskSummaries[drawerProgram.id] : undefined;
  const drawerPercent =
    drawerTaskSummary && drawerTaskSummary.totalLevels > 0
      ? Math.round((drawerTaskSummary.greenLevels / drawerTaskSummary.totalLevels) * 100)
      : undefined;
  const drawerTimeRemaining = drawerTaskSummary
    ? `~ ${drawerTaskSummary.timeRemainingMinutes} minutes left to complete`
    : '~ 25 minutes left to complete';
  const drawerCardModelText = drawerCardModel ? drawerCardModel.toString().toLowerCase() : '';
  const isMasterclassMentee = isMenteeDrawer && drawerCardModelText.includes('masterclass');
  const isMentorshipMentee = isMenteeDrawer && drawerCardModelText.includes('mentorship');
  const isMentorshipMentor = isMentorDrawer && drawerCardModelText.includes('mentorship');

  const isGuidedMasterclassCard = drawerProgramTag.label === 'Guided Masterclass';
  const isGuidedMasterclassMentor = isGuidedMasterclassCard && isMentorDrawer;
  const isGuidedMasterclassMentee = isGuidedMasterclassCard && isMenteeDrawer;
  const guidedMasterclassMentors = useMemo(() => {
    if (!isGuidedMasterclassMentee) return [];
    const seen = new Set<string>();
    const mentors: any[] = [];
    Object.values(objectiveMatchesMap || {}).forEach((matches) => {
      (Array.isArray(matches) ? matches : []).forEach((match: any) => {
        const mentor = match?.mentor || match;
        const matchId = mentor?.id || mentor?.userId || mentor?.mid || mentor?.matchId || mentor?._id || mentor?.uuid;
        if (!matchId || matchId === currentUserId || seen.has(matchId)) return;
        seen.add(matchId);
        mentors.push({
          ...mentor,
          matchedObjectives: match?.matchedObjectives || match?.extended?.learningObjectives || [],
          status: match?.status || match?.matchStatus || match?.matchStatusText || mentor?.status || 'Ongoing'
        });
      });
    });
    return mentors;
  }, [objectiveMatchesMap, isGuidedMasterclassMentee]);

  const showLeaderboard = drawerCardModelText.includes('masterclass');
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const leaderboardEntries = useMemo(() => {
    const source = leaderboardPeers.length ? leaderboardPeers : menteeList;
    const list = (source || [])
      .map((mentee) => {
        const progress = resolveGoalProgress(mentee);
        return {
          id: mentee?.mid || mentee?.userId || mentee?.id || mentee?.menteeId,
          displayName: mentee?.displayName || mentee?.name || mentee?.menteeName || 'Unnamed mentee',
          headshot: mentee?.headshot || mentee?.photo || mentee?.picture || 'https://placehold.co/40x40',
          goalProgress: progress,
          MCQScore: mentee.MCQScore,
          source: mentee
        };
      })
      .sort((a, b) => (b.goalProgress ?? 0) - (a.goalProgress ?? 0));

    if (drawerRole === 'mentee' && currentUserId) {
      const alreadyIncluded = list.some((entry) => entry.id && entry.id === currentUserId);
      if (!alreadyIncluded) {
        const myProgress = resolveGoalProgress(cardMenteeProgress[learningGrpId] || {});
        const myEntry = {
          id: currentUserId,
          displayName: getUserFriendlyName(userDetails),
          headshot: userDetails?.picture || userDetails?.photo || 'https://placehold.co/40x40',
          goalProgress: Math.min(Math.max(myProgress ?? 0, 0), 100),
          MCQScore: 0,
          isCurrentUser: true,
          source: { user: currentUserId }
        };
        list.push(myEntry);
      }
    }

    return list.sort((a, b) => (b.goalProgress ?? 0) - (a.goalProgress ?? 0));
  }, [leaderboardPeers, menteeList, drawerRole, currentUserId, learningGrpId, cardMenteeProgress, userDetails]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        p: '25px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        // gap: '80px',
        background: '#1C1C1C',
        borderRadius: '8px',
        outline: '1px solid #2A2A2A',
        outlineOffset: '-1px',
        boxSizing: 'border-box'
      }}
    >
      {columns.map((col, index) => {
        const cardsInColumn = grouped.filter((card) => {
          const status = getEffectiveStatus(card);
          return status === col.key;
        });
        return (
          <Box
            key={col.key}
            sx={{
              flex: '1 1 0',
              alignSelf: 'stretch',
              borderRadius: '5px',
              borderRight: index < columns.length - 1 ? '1px solid #2A2A2A' : 'none',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* HEADER DA COLUNA */}
            <Box
              sx={{
                alignSelf: 'stretch',
                px: '20px',
                py: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Typography
                  sx={{
                    color: '#B6B6B6',
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: '19.88px'
                  }}
                >
                  {col.title}
                </Typography>
                <InfoOutlinedIcon sx={{ color: '#444', fontSize: 18 }} />
              </Box>
            </Box>

            {/* LISTA DE CARDS */}
            <Box
              sx={{
                alignSelf: 'stretch',
                flex: '1 1 0',
                p: '10px',
                borderRadius: '5px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                overflow: 'auto',
                height: '100%'
              }}
            >
              {cardsInColumn.map((card) =>
                cardReady[card.id] ? renderCard(card, getEffectiveStatus(card)) : renderPlaceholderCard(card.id)
              )}
            </Box>
          </Box>
        );
      })}

      {/* DRAWER DETALHES DO PROGRAMA */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 600,
            background: '#151515 !important',
            borderLeft: '1px solid #2A2A2A !important',
            p: 0,
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {drawerProgram && (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              px: '25px !important',
              py: '20px !important',
              display: 'flex',
              flexDirection: 'column',
              gap: '30px !important',
              overflowY: 'auto'
            }}
          >
            {/* HEADER */}
            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px' }}>
              {/* TAG */}
              <Box
                sx={{
                  px: '8px',
                  py: '3px',
                  background: drawerProgramTag.borderColor,
                  borderRadius: '20px',
                  fontSize: 12,
                  color: drawerProgramTag.label === 'Masterclass Program' ? 'black' : '#FDFDFD',
                  fontFamily: 'Inter'
                }}
              >
                {drawerProgramTag.label}
              </Box>

              {/* ROLE */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>Program Role:</Typography>
                <Box
                  sx={{
                    px: '8px',
                    py: '5px',
                    background: '#444',
                    borderRadius: '6px',
                    color: '#FDFDFD',
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'capitalize'
                  }}
                >
                  {drawerRole}
                </Box>
              </Box>

              {/* CLOSE ICON */}
              <IconButton
                onClick={() => setDrawerOpen(false)}
                sx={{
                  ml: 'auto',
                  width: 32,
                  height: 32,
                  color: '#FDFDFD',
                  border: '1px solid #444',
                  borderRadius: '6px'
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* TITLE */}
            <Typography
              sx={{
                color: '#FDFDFD',
                fontSize: 26,
                fontWeight: 600,
                textTransform: 'capitalize'
              }}
            >
              {drawerProgram.name}
            </Typography>

            {/* ROLES BOX */}
            <Box
              sx={{
                width: '100%',
                p: '15px',
                background: '#0F0F0F',
                borderRadius: '5px',
                display: 'flex',
                flexDirection: 'column',
                gap: '15px'
              }}
            >
              {drawerProgram.roles.length > 1 && (
                <>
                  <Typography
                    sx={{
                      color: '#F79420',
                      fontSize: 12,
                      fontWeight: 700,
                      textAlign: 'center'
                    }}
                  >
                    You hold two roles in this program, please select which to view.
                  </Typography>

                  {/* ROLE SELECTOR */}
                  <Box
                    sx={{
                      width: '100%',
                      overflow: 'hidden',
                      borderRadius: '6px',
                      display: 'flex'
                    }}
                  >
                    {drawerProgram.roles.map((role) => {
                      const isSelected = drawerRole === role;
                      return (
                        <Box
                          key={role}
                          onClick={() => {
                            setDrawerRole(role);
                            setRoleSelections((prev) => ({ ...prev, [drawerProgram.id]: role }));
                            syncReduxForRole(drawerProgram, role);
                          }}
                          sx={{
                            flex: 1,
                            px: '18px',
                            py: '9px',
                            background: isSelected ? '#444444' : '#2A2A2A',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: isSelected ? 700 : 400,
                              color: isSelected ? '#FDFDFD' : '#B6B6B6',
                              textTransform: 'capitalize'
                            }}
                          >
                            {role}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </>
              )}

              {/* DESCRIPTION + FLAG ICON */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FlagOutlinedIcon sx={{ color: '#FDFDFD', fontSize: 18 }} />
                <Typography sx={{ color: '#FDFDFD', fontSize: 10, fontWeight: 700, fontFamily: 'Inter' }}>
                  {/* {drawerRole === 'mentor'
                    ? 'Find mentees whose objectives align with your expertise, message each one and schedule a mentorship meeting with them.'
                    : 'Find a mentor for each objective, message them, and complete at least one meeting with each.'} */}
                  {isMentorshipMentee &&
                    'Your goal is to complete each lesson in good time and apply your leanings wherever applicable.'}
                  {isMasterclassMentee &&
                    'Your goal is to complete each lesson by reading your lesson content, completing your lesson assessment, and marking your lesson as complete.'}
                  {isGuidedMasterclassMentor &&
                    'Your goal is to guide mentee matches through the completion of their program by messaging them to offer help and joining mentorship meetings where helpful.'}
                    {isMentorshipMentor && 'Your goal is to guide mentee matches through the completion of their program by messaging them to offer help and joining mentorship meetings where helpful.'}
                </Typography>
              </Box>
            </Box>

            {/* CTA BUTTON */}
            {!isGuidedMasterclassMentor && (
              <Button
                fullWidth
                onClick={() => handleManagePortfolio()}
                sx={{
                  px: '14px',
                  py: '10px',
                  borderRadius: '6px',
                  background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
                  color: '#FDFDFD',
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                {isMasterclassMentee ? 'Next Lesson' : isMentorshipMentee ? 'Continue Objectives' : 'Manage Portfolio'}
              </Button>
            )}

            {isMasterclassMentee && (
              <Box
                sx={{
                  background: '#0F0F0F'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1
                  }}
                >
                  <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>
                    {drawerPercent !== undefined ? `Program Progress: ${drawerPercent}%` : 'Program Progress'}
                  </Typography>
                  {drawerPercent !== undefined && (
                    <Typography sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 600 }}>{drawerPercent}%</Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    height: '5px',
                    borderRadius: '3px',
                    background: '#2A2A2A',
                    overflow: 'hidden'
                    // mb: 1
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      width: `${Math.min(Math.max(drawerPercent ?? 0, 0), 100)}%`,
                      background: '#F0B80D',
                      borderRadius: '3px'
                    }}
                  />
                </Box>
              </Box>
            )}

            {isMentorDrawer && (
              <>
                {drawerLoading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>
                      My Mentorship Stats:
                    </Typography>
                    {renderDrawerSkeletonLines(3)}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>
                      My Mentorship Stats:
                    </Typography>

                    {mentorshipStatRows.map((row, idx) => (
                      <Box key={idx} sx={{ display: 'flex', gap: '20px' }}>
                        {row.map((stat) => (
                          <Box key={stat.label} sx={{ flex: 1, p: '5px' }}>
                            <Typography
                              sx={{
                                color: '#B6B6B6',
                                fontSize: 12,
                                fontWeight: 700,
                                lineHeight: '14.40px'
                              }}
                            >
                              {stat.label}
                            </Typography>
                            <Typography
                              sx={{
                                color: '#FDFDFD',
                                fontSize: 18,
                                fontWeight: 700,
                                lineHeight: '21.60px'
                              }}
                            >
                              {stat.value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                )}
                <Box sx={{ width: '100%', height: '1px', background: '#444' }} />
              </>
            )}
            {isMenteeDrawer && !isMasterclassMentee && (
              <>
                {drawerLoading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>
                      My Mentorship Stats:
                    </Typography>
                    {renderDrawerSkeletonLines(3)}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>
                      My Mentorship Stats:
                    </Typography>

                    {menteeStatRows.map((row, idx) => (
                      <Box key={idx} sx={{ display: 'flex', gap: '20px' }}>
                        {row.map((stat) => (
                          <Box key={stat.label} sx={{ flex: 1, p: '5px' }}>
                            <Typography
                              sx={{
                                color: '#B6B6B6',
                                fontSize: 12,
                                fontWeight: 700,
                                lineHeight: '14.40px'
                              }}
                            >
                              {stat.label}
                            </Typography>
                            <Typography
                              sx={{
                                color: '#FDFDFD',
                                fontSize: 18,
                                fontWeight: 700,
                                lineHeight: '21.60px'
                              }}
                            >
                              {stat.value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                )}
                <Box sx={{ width: '100%', height: '1px', background: '#444' }} />
              </>
            )}
            {showLeaderboard && (
              <Box sx={{ width: '100%' }}>
                <Box sx={{ width: '100%', height: '1px', background: '#444', mb: '15px' }} />
                <Leaderboard data={leaderboardEntries} loader={leaderboardLoading && leaderboardEntries.length === 0} />
                <Box sx={{ width: '100%', height: '1px', background: '#444' }} />
              </Box>
            )}
            {isGuidedMasterclassMentor ? (
              drawerLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>My Mentees</Typography>
                  {renderDrawerSkeletonLines(3)}
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>My Mentees</Typography>
                    {(menteeList?.length || 0) === 0 ? (
                      <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>No mentees matched yet.</Typography>
                    ) : (
                      (menteeList || []).map((mentee, index) => {
                        const matchId =
                          mentee?.id ||
                          mentee?.menteeId ||
                          mentee?.userId ||
                          mentee?.matchId ||
                          `${mentee?.displayName || 'mentee'}-${mentee?._id || index}`;
                        const avatar =
                          mentee?.headshot || mentee?.photo || mentee?.picture || 'https://placehold.co/40x40';
                        const name = mentee?.name || mentee?.displayName || mentee?.menteeName || 'Unnamed mentee';
                        const statusValue =
                          mentee?.status || mentee?.matchStatus || mentee?.matchStatusText || 'in-progress';
                        const statusLabel = getStatusText(statusValue);
                        const statusColor = getStatusColor(statusValue);
                        const objectiveTitle =
                          (mentee?.matchedObjectives || [])[0]?.objective ||
                          (mentee?.matchedObjectives || [])[0]?.name ||
                          'Match ativo';
                        const completionPercent = getCompletionPercent(mentee);
                        return (
                          <Box
                            key={matchId}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px',
                              py: '12px'
                              // borderBottom: '1px solid #2A2A2A'
                            }}
                          >
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <Box
                                  component="img"
                                  src={avatar}
                                  alt={name}
                                  sx={{ width: 44, height: 44, borderRadius: '50%' }}
                                />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                  <Typography
                                    sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 400, fontFamily: 'Inter' }}
                                  >
                                    {name}
                                  </Typography>
                                  {/* <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>{objectiveTitle}</Typography> */}
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {/* <Typography sx={{ color: '#B6B6B6', fontSize: 11, textTransform: 'uppercase' }}>
                                  {`${completionPercent}% completo`}
                                </Typography> */}
                                {/* <Button
                                  variant="outlined"
                                  size="small"
                                  sx={{
                                    color: '#FDFDFD',
                                    // borderColor: '#444444',
                                    textTransform: 'none',
                                    // borderRadius: '50%',
                                    fontSize: 11,
                                    height: '20px'  ,
                                    width: '20px'  ,
                                    // px: 1.5,
                                    // py: 0.25
                                  }}
                                  onClick={() => handleStartConversation(mentor)}
                                > */}
                                <Box
                                  onClick={() => handleStartConversation(mentee)}
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    padding: '7px',
                                   background: 'linear-gradient(180deg, var(--orange-500, #F79420) 0%, var(--orange-700, #F15A26) 100%)',
                                    borderRadius: '17px',
                                    display: 'inline-flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.9 }
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: '20px',
                                      height: '20px',
                                      padding: '2px',
                                      position: 'relative',
                                      overflow: 'hidden',
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center'
                                    }}
                                  >
                                    {/* Ícone no centro — agora visível e colorido */}
                                    <QuestionAnswerOutlinedIcon
                                      sx={{
                                        zIndex: 2,
                                        fontSize: '18px',
                                        color: 'white' // <<< COR DO ÍCONE DEFINIDA AQUI
                                      }}
                                    />
                                  </Box>
                                </Box>
                                {/* </Button> */}
                                <Box
                                  onClick={(event) => handleOpenMenu(event, mentee)}
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    padding: '7px',
                                    background: 'var(--Fill-Button-button-contrast, #CDCDCD)',
                                    borderRadius: '17px',
                                    display: 'inline-flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.9 }
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: '20px',
                                      height: '20px',
                                      padding: '2px',
                                      position: 'relative',
                                      overflow: 'hidden',
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center'
                                    }}
                                  >
                                    {/* Ícone no centro — agora visível e colorido */}
                                    <MoreHorizIcon
                                      sx={{
                                        zIndex: 2,
                                        fontSize: '18px',
                                        color: '#0F0F0F' // <<< COR DO ÍCONE DEFINIDA AQUI
                                      }}
                                    />
                                  </Box>
                                </Box>
                                {/* <IconButton
                                  size="small"
                                  onClick={(event) => handleOpenMenu(event, mentor)}
                                  sx={{ color: '#FDFDFD' }}
                                >
                                  <MoreHorizIcon fontSize="small" />
                                </IconButton> */}
                              </Box>
                            </Box>
                            {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography
                                sx={{
                                  color: statusColor || '#868686',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  textTransform: 'uppercase'
                                }}
                              >
                                {statusLabel}
                              </Typography>
                              <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>{objectiveTitle}</Typography>
                            </Box> */}
                            {/* <LinearProgress
                              variant="determinate"
                              value={completionPercent}
                              sx={{
                                height: 4,
                                borderRadius: '999px',
                                backgroundColor: '#0F0F0F',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: '#F79420'
                                }
                              }}
                            /> */}
                          </Box>
                        );
                        return (
                          <Box
                            key={matchId}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px',
                              py: '12px',
                              borderBottom: '1px solid #2A2A2A'
                            }}
                          >
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <Box
                                  component="img"
                                  src={avatar}
                                  alt={name}
                                  sx={{ width: 44, height: 44, borderRadius: '50%' }}
                                />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                  <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 600 }}>
                                    {name}
                                  </Typography>
                                  <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>{objectiveTitle}</Typography>
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ color: '#B6B6B6', fontSize: 11, textTransform: 'uppercase' }}>
                                  {`${completionPercent}% completo`}
                                </Typography>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  sx={{
                                    color: '#FDFDFD',
                                    borderColor: '#444444',
                                    textTransform: 'none',
                                    fontSize: 11,
                                    px: 1.5,
                                    py: 0.25
                                  }}
                                  onClick={() => handleStartConversation(mentee)}
                                >
                                  Start Conversation
                                </Button>
                                <IconButton
                                  size="small"
                                  onClick={(event) => handleOpenMenu(event, mentee)}
                                  sx={{ color: '#FDFDFD' }}
                                >
                                  <MoreHorizIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography
                                sx={{
                                  color: statusColor || '#868686',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  textTransform: 'uppercase'
                                }}
                              >
                                {statusLabel}
                              </Typography>
                              <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>{objectiveTitle}</Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={completionPercent}
                              sx={{
                                height: 4,
                                borderRadius: '999px',
                                backgroundColor: '#0F0F0F',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: '#F79420'
                                }
                              }}
                            />
                          </Box>
                        );
                      })
                    )}
                  </Box>
                  <Box sx={{ width: '100%', height: '1px', background: '#444' }} />
                </>
              )
            ) : isGuidedMasterclassMentee ? (
              drawerLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>My Mentors</Typography>
                  {renderDrawerSkeletonLines(3)}
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700, fontFamily: 'Inter' }}>
                      My Mentors:
                    </Typography>
                    {menteeList.length === 0 ? (
                      <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>No mentors matched yet.</Typography>
                    ) : (
                      menteeList.map((mentor, index) => {
                        const matchId =
                          mentor?.id ||
                          mentor?.userId ||
                          mentor?.mid ||
                          mentor?.matchId ||
                          `${mentor?.displayName || 'mentor'}-${mentor?._id || index}`;
                        const avatar =
                          mentor?.headshot || mentor?.photo || mentor?.avatar || 'https://placehold.co/40x40';
                        const name = mentor?.name || mentor?.displayName || 'Unnamed mentor';
                        const statusValue =
                          mentor?.status || mentor?.matchStatus || mentor?.matchStatusText || 'in-progress';
                        const statusLabel = getStatusText(statusValue);
                        const statusColor = getStatusColor(statusValue);
                        const objectiveTitle =
                          (mentor?.matchedObjectives || [])[0]?.objective ||
                          (mentor?.matchedObjectives || [])[0]?.name ||
                          'Active match';
                        const completionPercent = getCompletionPercent(mentor);

                        return (
                          <Box
                            key={matchId}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px',
                              py: '12px'
                              // borderBottom: '1px solid #2A2A2A'
                            }}
                          >
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <Box
                                  component="img"
                                  src={avatar}
                                  alt={name}
                                  sx={{ width: 44, height: 44, borderRadius: '50%' }}
                                />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                  <Typography
                                    sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 400, fontFamily: 'Inter' }}
                                  >
                                    {name}
                                  </Typography>
                                  {/* <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>{objectiveTitle}</Typography> */}
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {/* <Typography sx={{ color: '#B6B6B6', fontSize: 11, textTransform: 'uppercase' }}>
                                  {`${completionPercent}% completo`}
                                </Typography> */}
                                {/* <Button
                                  variant="outlined"
                                  size="small"
                                  sx={{
                                    color: '#FDFDFD',
                                    // borderColor: '#444444',
                                    textTransform: 'none',
                                    // borderRadius: '50%',
                                    fontSize: 11,
                                    height: '20px'  ,
                                    width: '20px'  ,
                                    // px: 1.5,
                                    // py: 0.25
                                  }}
                                  onClick={() => handleStartConversation(mentor)}
                                > */}
                                <Box
                                  onClick={() => handleStartConversation(mentor)}
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    padding: '7px',
                                    background: 'var(--Fill-Button-button-contrast, #CDCDCD)',
                                    borderRadius: '17px',
                                    display: 'inline-flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.9 }
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: '20px',
                                      height: '20px',
                                      padding: '2px',
                                      position: 'relative',
                                      overflow: 'hidden',
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center'
                                    }}
                                  >
                                    {/* Ícone no centro — agora visível e colorido */}
                                    <QuestionAnswerOutlinedIcon
                                      sx={{
                                        zIndex: 2,
                                        fontSize: '18px',
                                        color: '#0F0F0F' // <<< COR DO ÍCONE DEFINIDA AQUI
                                      }}
                                    />
                                  </Box>
                                </Box>
                                {/* </Button> */}
                                <Box
                                  onClick={(event) => handleOpenMenu(event, mentor)}
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    padding: '7px',
                                    background: 'var(--Fill-Button-button-contrast, #CDCDCD)',
                                    borderRadius: '17px',
                                    display: 'inline-flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.9 }
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: '20px',
                                      height: '20px',
                                      padding: '2px',
                                      position: 'relative',
                                      overflow: 'hidden',
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center'
                                    }}
                                  >
                                    {/* Ícone no centro — agora visível e colorido */}
                                    <MoreHorizIcon
                                      sx={{
                                        zIndex: 2,
                                        fontSize: '18px',
                                        color: '#0F0F0F' // <<< COR DO ÍCONE DEFINIDA AQUI
                                      }}
                                    />
                                  </Box>
                                </Box>
                                {/* <IconButton
                                  size="small"
                                  onClick={(event) => handleOpenMenu(event, mentor)}
                                  sx={{ color: '#FDFDFD' }}
                                >
                                  <MoreHorizIcon fontSize="small" />
                                </IconButton> */}
                              </Box>
                            </Box>
                            {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography
                                sx={{
                                  color: statusColor || '#868686',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  textTransform: 'uppercase'
                                }}
                              >
                                {statusLabel}
                              </Typography>
                              <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>{objectiveTitle}</Typography>
                            </Box> */}
                            {/* <LinearProgress
                              variant="determinate"
                              value={completionPercent}
                              sx={{
                                height: 4,
                                borderRadius: '999px',
                                backgroundColor: '#0F0F0F',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: '#F79420'
                                }
                              }}
                            /> */}
                          </Box>
                        );
                      })
                    )}
                  </Box>
                  {/* <Box sx={{ width: '100%', height: '1px', background: '#444' }} /> */}
                </>
              )
            ) : (isMentorDrawer || isMenteeDrawer) && !isMasterclassMentee ? (
              drawerLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>
                      {isMenteeDrawer ? 'Learning Objectives and Mentors' : 'My Expertise and Mentees:'}
                    </Typography>
                  </Box>
                  {renderDrawerSkeletonLines(4)}
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>
                        {isMenteeDrawer ? 'Learning Objectives and Mentors' : 'My Expertise and Mentees:'}
                      </Typography>
                      {isMentorDrawer && (
                        <>
                          <InfoOutlinedIcon sx={{ color: '#444', fontSize: 18 }} />
                          <Box
                            sx={{
                              ml: 'auto',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px',
                              cursor: 'pointer'
                            }}
                            onClick={() => setObjectiveModalOpen(true)}
                          >
                            <Typography sx={{ color: '#F79420', fontSize: 12, fontWeight: 600 }}>
                              Add Expertise
                            </Typography>
                            <AddIcon sx={{ color: '#F79420', fontSize: 16 }} />
                          </Box>
                        </>
                      )}
                      {isMenteeDrawer && (
                        <Box
                          sx={{
                            ml: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            cursor: 'pointer'
                          }}
                          onClick={() => setObjectiveModalOpen(true)}
                        >
                          <Typography sx={{ color: '#F79420', fontSize: 12, fontWeight: 600 }}>
                            Add Learning Objective
                          </Typography>
                          <AddIcon sx={{ color: '#F79420', fontSize: 16 }} />
                        </Box>
                      )}
                    </Box>

                    {createdLearningObjectivesList.length === 0 ? (
                      <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>
                        No learning objectives available yet.
                      </Typography>
                    ) : (
                      createdLearningObjectivesList.map((obj: any, idx: number) => {
                        const objectiveKey =
                          getObjectiveIdentifier(obj) ||
                          obj?.id ||
                          obj?._id ||
                          obj?.objectiveId ||
                          obj?.objective ||
                          obj?.name ||
                          `${obj?.objective || obj?.name || 'objective'}_${idx}`;

                        const objectiveEntry = existingKeys.find((entry: any) => {
                          const candidateIds = [
                            entry?.objectiveId,
                            entry?.id,
                            entry?._id,
                            entry?.objective,
                            entry?.name
                          ]
                            .filter(Boolean)
                            .map((id: any) => String(id));
                          return candidateIds.some((id: string) => id === String(objectiveKey));
                        });

                        const mentorsFromEntry =
                          isMenteeDrawer && objectiveEntry?.mentors?.length
                            ? objectiveEntry.mentors.map((mentor: any) => ({
                                ...mentor,
                                matchedObjectives: [
                                  {
                                    objective: objectiveEntry?.objective || objectiveEntry?.name || '',
                                    id: objectiveKey,
                                    objectiveId: objectiveKey,
                                    status: objectiveEntry?.status || 'Ongoing'
                                  }
                                ],
                                objectiveLabel: objectiveEntry?.objective || objectiveEntry?.name || '',
                                objectiveStatus: objectiveEntry?.status || 'Ongoing',
                                objectiveId: objectiveKey
                              }))
                            : [];

                        const menteesPerObjective = mentorsFromEntry.length
                          ? mentorsFromEntry
                          : isMenteeDrawer && drawerObjectiveMentors[objectiveKey]?.length
                          ? drawerObjectiveMentors[objectiveKey]
                          : getMenteesByObjectiveId(menteeList, objectiveKey);

                        const objId = obj.id;
                        const expanded = !!expandedObjectives[objId];

                        return (
                          <Box key={objId} sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                justifyContent: 'space-between'
                              }}
                              onClick={() =>
                                setExpandedObjectives((prev) => ({
                                  ...prev,
                                  [objId]: !expanded
                                }))
                              }
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <Typography
                                  sx={{
                                    color: '#B6B6B6',
                                    fontSize: 18,
                                    fontWeight: 700
                                  }}
                                >
                                  {idx + 1}
                                </Typography>

                                <Typography
                                  sx={{
                                    color: '#F79420',
                                    fontSize: 12,
                                    fontWeight: 700
                                  }}
                                >
                                  {obj?.objective || `Objective ${idx + 1}`}
                                </Typography>
                              </Box>

                              <KeyboardArrowDownIcon
                                sx={{
                                  color: '#FDFDFD',
                                  fontSize: 20,
                                  transform: expanded ? 'rotate(180deg)' : 'none',
                                  transition: '0.2s'
                                }}
                              />
                            </Box>

                            {expanded && (
                              <Box
                                sx={{
                                  pl: '30px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '15px'
                                }}
                              >
                                <Button
                                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                                  onClick={() => {
                                    setRecruitObjId(objId);
                                    setRecruitObjectiveName(obj?.objective || `Objective ${idx + 1}`);
                                    setRecruitModalOpen(true);
                                  }}
                                  sx={{
                                    width: 'fit-content',
                                    background: '#1C1C1C',
                                    color: '#F79420',
                                    fontSize: 12,
                                    border: '1px solid #444',
                                    borderRadius: '6px',
                                    textTransform: 'none',
                                    px: 2,
                                    py: 0.75
                                  }}
                                >
                                  Find New {isMenteeDrawer ? 'Mentor' : 'Mentee'}
                                </Button>

                                {menteesPerObjective.length === 0 ? (
                                  <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>
                                    No {isMenteeDrawer ? 'mentors' : 'mentees'} matched yet.
                                  </Typography>
                                ) : (
                                  menteesPerObjective.map((match: any) => {
                                    const person =
                                      (isMenteeDrawer ? match?.mentor : match?.mentee) ||
                                      match?.mentor ||
                                      match?.mentee ||
                                      match;
                                    const matchId = person?.id || match?.mid || match?.userId;
                                    const avatar = person?.headshot || person?.avatar || 'https://placehold.co/40x40';
                                    const name =
                                      person?.displayName ||
                                      person?.name ||
                                      (isMenteeDrawer ? 'Unnamed mentor' : 'Unnamed mentee');
                                    const objectiveEntry =
                                      (match?.matchedObjectives || match?.extended?.learningObjectives || []).find(
                                        (x: any) =>
                                          String(x.id || x._id || x.objective || x.name) ===
                                          String(obj.id || obj._id || obj.objective || obj.name)
                                      ) || {};
                                    const status =
                                      objectiveEntry?.status ||
                                      match?.status ||
                                      match?.matchStatus ||
                                      match?.matchStatusText ||
                                      'Ongoing';

                                    return (
                                      <Box
                                        key={matchId || `${match?.name || 'match'}-${idx}`}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between'
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                          <img src={avatar} style={{ width: 40, height: 40, borderRadius: '50%' }} />
                                          <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>
                                            {name}
                                          </Typography>
                                        </Box>

                                        <Typography
                                          sx={{
                                            color: status === 'Completed' ? '#00972D' : '#B6B6B6',
                                            fontSize: 12,
                                            fontWeight: 600
                                          }}
                                        >
                                          {status}
                                        </Typography>
                                      </Box>
                                    );
                                  })
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      })
                    )}
                  </Box>
                  <Box sx={{ width: '100%', height: '1px', background: '#444' }} />
                </>
              )
            ) : (
              // <Box sx={{ width: '100%', height: '1px', background: '#444' }} />
              <div />
            )}
          </Box>
        )}
      </Drawer>

      {/* CONTEXT MENU */}
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
        {isMentorDrawer && <MenuItem onClick={handleViewMenteeProgress}>View mentee progress</MenuItem>}
        <MenuItem
          onClick={() => {
            if (menuMentee) handleViewProfile(menuMentee);
          }}
        >
          {isMenteeDrawer ? 'View mentor profile' : 'View mentee profile'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuMentee) handleMeetingHistory(menuMentee);
          }}
        >
          Meeting history
        </MenuItem>
      </Menu>

      <Modal open={meetingModalOpen} onClose={handleCloseMeetingModal}>
        <Box
          sx={{
            width: 500,
            maxHeight: '80vh',
            overflowY: 'auto',
            bgcolor: 'var(--Fill-Modal-background, #0F0F0F)',
            borderRadius: 2,
            outline: '1px solid var(--Stroke-Modal-border, #444444)',
            outlineOffset: '-1px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            p: 3
          }}
        >
          <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700, mb: 2 }}>
            Meeting History with {meetingMentee?.name || meetingMentee?.displayName || meetingMentee?.menteeName || ''}
          </Typography>
          {(meetingMentee?.meetings || []).length ? (
            (meetingMentee.meetings || []).map((meeting: any, idx: number) => (
              <Box key={`${meeting?.id || idx}-${meetingMentee?.id || 'mentee'}`} mb={2}>
                <MeetingDetails
                  meeting={meeting}
                  index={idx + 1}
                  menteeName={meetingMentee?.name || meetingMentee?.displayName || meetingMentee?.menteeName || ''}
                  myMenteesList={meetingMentee}
                  mentorsList={[]}
                  menteeData={meetingMentee}
                />
              </Box>
            ))
          ) : (
            <Typography sx={{ color: '#B6B6B6', fontSize: 14 }}>No meeting history available.</Typography>
          )}
        </Box>
      </Modal>

      <UserProfileDetailsPopup
        loading={false}
        userData={profileUser}
        openUserProfileDetails={profileModalOpen}
        handleopenUserProfileDetails={(flag: boolean) => setProfileModalOpen(flag)}
      />

      {/* MODAL DE OBJETIVO / EXPERTISE */}
      <Modal open={objectiveModalOpen} onClose={() => setObjectiveModalOpen(false)}>
        <Box
          sx={{
            width: 550,
            bgcolor: 'var(--Fill-Modal-background, #0F0F0F)',
            borderRadius: 2,
            outline: '1px solid var(--Stroke-Modal-border, #444444)',
            outlineOffset: '-1px',
            overflow: 'hidden',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}
        >
          <Box
            sx={{
              width: '100%',
              px: 3,
              pr: 5,
              py: 2.5,
              borderBottom: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
              display: 'flex',
              alignItems: 'center',
              gap: 2.5,
              position: 'relative'
            }}
          >
            <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700, flex: 1 }}>
              {drawerRole === 'mentor' ? 'Add Area of Expertise' : 'Add Learning Objective'}
            </Typography>
            <Button
              onClick={() => {
                setObjectiveModalOpen(false);
                setSelectedObjectiveId('');
              }}
              sx={{
                minWidth: 32,
                height: 32,
                color: '#FDFDFD',
                border: '1px solid #444',
                borderRadius: '6px'
              }}
            >
              X
            </Button>
          </Box>
          <Box
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5
            }}
          >
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 500 }}>
                {drawerRole === 'mentor' ? 'Area of Expertise *' : 'Learning Objective *'}
              </Typography>
              <Select
                value={selectedObjectiveId}
                onChange={(e) => setSelectedObjectiveId(String(e.target.value))}
                displayEmpty
                sx={{
                  background: 'rgba(0,0,0,0.70)',
                  borderRadius: '6px',
                  border: '1px solid #868686',
                  color: '#FDFDFD'
                }}
              >
                <MenuItem value="">
                  <em>Please Select</em>
                </MenuItem>
                {objectivesUserCanAdd.length === 0 ? (
                  <MenuItem value="" disabled>
                    No objectives available
                  </MenuItem>
                ) : (
                  objectivesUserCanAdd.map((obj: any) => {
                    const optionValue =
                      getObjectiveIdentifier(obj) || (typeof obj === 'string' ? obj : obj?.objective || obj?.name);
                    const optionLabel = typeof obj === 'string' ? obj : obj?.objective || obj?.name || 'Objective';
                    if (!optionValue) return null;
                    return (
                      <MenuItem key={`${optionValue}-${optionLabel}`} value={optionValue}>
                        {optionLabel}
                      </MenuItem>
                    );
                  })
                )}
              </Select>
            </Box>

            <Box display="flex" gap={1.5} justifyContent="center">
              <Button
                fullWidth
                onClick={() => {
                  setObjectiveModalOpen(false);
                  setSelectedObjectiveId('');
                }}
                sx={{
                  px: 1.75,
                  py: 1.25,
                  background: 'var(--Fill-Button-button-basic-default, #1C1C1C)',
                  borderRadius: '6px',
                  color: 'var(--Stroke-Button-button-basic-default, #FDFDFD)',
                  fontSize: 13,
                  fontWeight: 500,
                  textTransform: 'none'
                }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                disabled={!selectedObjectiveId || savingObjective}
                sx={{
                  px: 1.75,
                  py: 1.25,
                  background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
                  borderRadius: '6px',
                  color: 'var(--Stroke-Button-button-cta-default, #FDFDFD)',
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
                onClick={handleAddObjective}
              >
                {savingObjective ? 'Saving...' : 'Add'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <RecruitNewMentor
        open={recruitModalOpen}
        onClose={() => setRecruitModalOpen(false)}
        title={`Find a ${drawerRole === 'mentee' ? 'mentee' : 'mentor'}`}
        mentorsList={[]}
        onProfileClick={() => {}}
        onChatClick={(each) => handleStartConversation(each)}
        onCalendarClick={() => {}}
        objId={recruitObjId}
        grpId={learningGrpId}
        apiType={`${drawerRole === 'mentee' ? 'mentor' : 'mentee'}`}
        learningObjective={createdLearningObjectivesList.find((lo: any) => lo.id === recruitObjId)}
      />
    </Box>
  );
}
